import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

// ---------------------------------------------------------------------------
// Decoder logic (ported from packet_interpreter_dragino_lht65.html)
// ---------------------------------------------------------------------------

interface DecoderType {
  ext: number;
  timeReq: boolean;
  timeSync: boolean;
  pollMsg: boolean;
  hasBattV: boolean;
  hasBattP: boolean;
  hasTimestamp: boolean;
}

interface DecoderResult {
  rows: (string | number)[][];
  battV: string;
  battP: string;
  extType: string;
}

function getExtType(ext: number): string {
  switch (ext) {
    case 0x01:
      return 'DS18B20';
    case 0x04:
      return 'DryContact Interrupt';
    case 0x05:
      return 'Illumination Sensor';
    case 0x06:
      return 'ADC Sensor';
    case 0x07:
      return 'Pulse counter 16bits';
    case 0x09:
      return 'DS18B20 Timestamp';
    default:
      return 'None';
  }
}

function decodeDragino(bytes: Uint8Array): DecoderResult {
  const fixedSize = 11;

  // Determine packet type from byte 6 of the first record
  const type: DecoderType = {
    ext: bytes[6] & 0x0f,
    timeReq: Boolean(bytes[6] & 0x10),
    timeSync: Boolean(bytes[6] & 0x20),
    pollMsg: Boolean(bytes[6] & 0x40),
    hasBattV: true,
    hasBattP: true,
    hasTimestamp: false,
  };

  if (type.ext === 0x09) {
    type.hasBattV = false;
    type.hasBattP = true;
    type.hasTimestamp = true;
  }
  if (type.pollMsg) {
    type.hasBattP = false;
    type.hasBattV = false;
    type.hasTimestamp = true;
  }

  // Battery
  let battV = '';
  let battP = '';
  if (type.hasBattV) {
    const voltage = ((bytes[0] << 8 | bytes[1]) & 0x3fff) / 1000;
    battV = voltage.toString() + 'V';
  }
  if (type.hasBattP) {
    const percentage = ((bytes[0] >> 6) & 0xff) * 33;
    battP = percentage.toString() + '%';
  }

  const rows: (string | number)[][] = [];
  let index = 0;
  let timestamp: string = '';

  while (index < bytes.length) {
    const row: (string | number)[] = [];

    // Timestamp
    if (type.hasTimestamp) {
      const epochMs =
        ((bytes[index + 7] << 24) +
          (bytes[index + 8] << 16) +
          (bytes[index + 9] << 8) +
          bytes[index + 10]) *
        1000;
      timestamp = new Date(epochMs).toISOString();
    }
    row.push(timestamp);

    // Built-in temperature (stream 1)
    // eslint-disable-next-line no-bitwise
    const temp = (bytes[index + 2] << 24 >> 16 | bytes[index + 3]) / 100;
    if (temp !== null && temp !== 327.67) row.push(temp);

    // Built-in humidity (stream 2)
    const hum = ((bytes[index + 4] << 8 | bytes[index + 5]) & 0xfff) / 10;
    if (hum !== null && hum !== 327.67) row.push(hum);

    // External value
    let hiByte: number;
    let loByte: number;
    if (type.hasTimestamp) {
      hiByte = bytes[index + 0];
      loByte = bytes[index + 1];
    } else {
      hiByte = bytes[index + 7];
      loByte = bytes[index + 8];
    }

    let extValue: number | null = null;
    switch (type.ext) {
      case 0x01:
        // eslint-disable-next-line no-bitwise
        extValue = (hiByte << 24 >> 16 | loByte) / 100;
        break;
      case 0x04:
        extValue = hiByte ? 1 : 0;
        break;
      case 0x05:
        extValue = (hiByte << 8) | loByte;
        break;
      case 0x06:
        extValue = ((hiByte << 8) | loByte) / 1000;
        break;
      case 0x07:
        extValue = (hiByte << 8) | loByte;
        break;
      case 0x09:
        // eslint-disable-next-line no-bitwise
        extValue = (hiByte << 24 >> 16 | loByte) / 100;
        break;
      default:
        extValue = null;
    }
    if (extValue !== null && extValue !== 327.67) row.push(extValue);

    rows.push(row);
    index += fixedSize;
  }

  return { rows, battV, battP, extType: getExtType(type.ext) };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function PacketInterpreterDraginoPage() {
  const [packetInput, setPacketInput] = useState('');
  const [result, setResult] = useState<DecoderResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const handleSubmit = () => {
    setParseError(null);
    const matches = packetInput.replace(/\s/g, '').match(/[\da-f]{2}/gi);
    if (!matches || matches.length === 0) {
      setParseError('Invalid packet. No valid hex bytes found.');
      setResult(null);
      return;
    }
    try {
      const bytes = new Uint8Array(matches.map((h) => parseInt(h, 16)));
      const decoded = decodeDragino(bytes);
      setResult(decoded);
    } catch (err) {
      setParseError('Failed to decode packet: ' + String(err));
      setResult(null);
    }
  };

  // Populate from ?packet=<encoded-hex> on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const packetParam = params.get('packet');
    if (packetParam) {
      const decoded = decodeURIComponent(packetParam);
      setPacketInput(decoded);
      const matches = decoded.replace(/\s/g, '').match(/[\da-f]{2}/gi);
      if (matches && matches.length > 0) {
        try {
          const bytes = new Uint8Array(matches.map((h) => parseInt(h, 16)));
          setResult(decodeDragino(bytes));
        } catch {
          // silently ignore on mount
        }
      }
    }
  }, []);

  // Determine number of value columns (exclude timestamp at index 0)
  const maxValueCols = result
    ? Math.max(0, ...result.rows.map((r) => r.length - 1))
    : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Dragino LHT65 Packet Interpreter</h1>
        <p className="text-muted-foreground mt-1">
          Decode Dragino LHT65 LoRaWAN sensor packets (11-byte records).
        </p>
      </div>

      {/* Input section */}
      <Card>
        <CardHeader>
          <CardTitle>Packet Input</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="packet-input">Hex Packet</Label>
            <Textarea
              id="packet-input"
              rows={4}
              placeholder="Paste hex packet here..."
              value={packetInput}
              onChange={(e) => setPacketInput(e.target.value)}
            />
          </div>
          <Button onClick={handleSubmit}>Submit</Button>

          {parseError && (
            <p className="text-sm text-destructive">{parseError}</p>
          )}
        </CardContent>
      </Card>

      {/* Parsed header fields */}
      {result && (
        <Card>
          <CardHeader>
            <CardTitle>Packet Information</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="space-y-1">
              <Label htmlFor="datas">Datas in Packet</Label>
              <Input id="datas" value={result.rows.length} disabled />
            </div>
            <div className="space-y-1">
              <Label htmlFor="battV">Battery Voltage</Label>
              <Input id="battV" value={result.battV || '—'} disabled />
            </div>
            <div className="space-y-1">
              <Label htmlFor="battP">Battery Percentage</Label>
              <Input id="battP" value={result.battP || '—'} disabled />
            </div>
            <div className="space-y-1">
              <Label htmlFor="type">Type</Label>
              <Input id="type" value={result.extType} disabled />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data rows table */}
      {result && result.rows.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Data Records</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="border-r">Timestamp</TableHead>
                    {Array.from({ length: maxValueCols }, (_, i) => (
                      <TableHead key={i}>Value {i + 1}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.rows.map((row, idx) => {
                    const [ts, ...values] = row;
                    return (
                      <TableRow key={idx} className="even:bg-muted/30">
                        <TableCell className="border-r whitespace-nowrap">
                          {ts
                            ? new Date(ts as string).toLocaleString('sv-SE')
                            : '—'}
                        </TableCell>
                        {Array.from({ length: maxValueCols }, (_, vi) => {
                          const val = values[vi];
                          return (
                            <TableCell key={vi}>
                              {val !== undefined && val !== null
                                ? (val as number).toFixed(2)
                                : ''}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
